import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  FileText, RefreshCw, Download, Lock, Unlock, Sparkles, Plus, X,
  Users, TrendingUp, MessageSquare, Link2, Image as ImageIcon, ExternalLink,
} from "lucide-react";
import { api } from "../../lib/api";
import { fmtDatePT } from "../../lib/time";
import { resolveImageUrl } from "../../lib/media";

const Overline = ({ children }) => (
  <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono">{children}</div>
);

const LESSON_KEYS = [
  { k: "continue", label: "Continue doing" },
  { k: "change", label: "Change" },
  { k: "logistics", label: "Logistics" },
  { k: "timing", label: "Timing" },
  { k: "venue", label: "Venue" },
  { k: "food", label: "Food" },
  { k: "format", label: "Format" },
  { k: "invitations", label: "Invitations" },
  { k: "facilitation", label: "Facilitation" },
];

export default function ReportTab({ ev }) {
  const [data, setData] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const [exporting, setExporting] = useState(false);
  const saveTimers = useRef({});

  const load = async () => {
    try {
      const r = await api.get(`/admin/events/${ev.id}/report`);
      setData(r.data);
    } catch { toast.error("Failed to load report"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ev.id]);

  const generatedAt = useMemo(() => data?.auto?.generated_at ? new Date(data.auto.generated_at).toLocaleString() : "-", [data]);

  if (!data) return <div className="skeleton h-96" />;

  const { report, auto, media, event } = data;
  const isFinal = report.status === "final";


  // Debounced autosave
  const saveField = (key, value) => {
    setData((d) => ({ ...d, report: { ...d.report, [key]: value } }));
    clearTimeout(saveTimers.current[key]);
    saveTimers.current[key] = setTimeout(async () => {
      setSavingKey(key);
      try { await api.patch(`/admin/events/${ev.id}/report`, { [key]: value }); }
      catch (e) { toast.error(e.response?.data?.detail || "Autosave failed"); }
      finally { setSavingKey(null); }
    }, 700);
  };

  const setLesson = (k, v) => {
    const next = { ...(report.lessons || {}), [k]: v };
    saveField("lessons", next);
  };
  const setEngagement = (k, v) => {
    const next = { ...(report.marketing_engagement || {}), [k]: v };
    saveField("marketing_engagement", next);
  };
  const addGuest = () => saveField("notable_guests", [...(report.notable_guests || []), { _k: `g_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, name: "", org: "", type: "member" }]);
  const setGuest = (i, k, v) => {
    const next = [...(report.notable_guests || [])];
    next[i] = { ...next[i], [k]: v };
    saveField("notable_guests", next);
  };
  const removeGuest = (i) => {
    const next = [...(report.notable_guests || [])];
    next.splice(i, 1);
    saveField("notable_guests", next);
  };

  const addLink = async (url) => {
    if (!url || !url.trim()) return;
    try {
      await api.post(`/admin/events/${ev.id}/report/media`, { kind: "link", url: url.trim() });
      load();
    } catch { toast.error("Failed to add link"); }
  };
  const uploadImage = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const up = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/admin/events/${ev.id}/report/media`, { kind: "image", url: up.data.url });
      load();
    } catch { toast.error("Upload failed"); }
  };
  const removeMedia = async (mid) => {
    if (!window.confirm("Remove this from the report?")) return;
    try { await api.delete(`/admin/events/${ev.id}/report/media/${mid}`); load(); }
    catch { toast.error("Failed to remove"); }
  };

  const toggleFinal = async () => {
    const next = isFinal ? "draft" : "final";
    try { await api.patch(`/admin/events/${ev.id}/report`, { status: next }); load(); toast.success(next === "final" ? "Report marked Final and locked" : "Report unlocked as Draft"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const r = await api.post(`/admin/events/${ev.id}/report/export`, {}, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `venturedock-report-${ev.short_slug || ev.id}-v${report.version}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Version ${report.version} PDF ready`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Export failed"); }
    finally { setExporting(false); }
  };

  return (
    <div className="grid lg:grid-cols-[1fr_300px] gap-6" data-testid="report-tab">
      <div className="space-y-6">
        {/* Cover */}
        <div className="glass-panel p-6" data-testid="report-cover">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <Overline>Post-event report</Overline>
              <div className="mt-2 font-heading text-2xl font-medium">{event.title}</div>
              <div className="text-xs text-muted-foreground mt-1 font-mono">
                {fmtDatePT(event.start_at, { weekday: "long", month: "long", day: "numeric", year: "numeric" })} · Version {report.version} · {report.status.toUpperCase()}
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <RefreshCw size={11} />
              <span className="font-mono">Data as of {generatedAt}</span>
              <button onClick={load} className="btn-ghost text-xs" data-testid="report-refresh">Refresh</button>
            </div>
          </div>
        </div>

        {/* Section: Event Summary */}
        <ReportSection title="Event Summary" testId="rp-summary">
          <StatRow>
            <Stat label="Registered" value={auto.attendance?.registered} />
            <Stat label="Checked in" value={auto.attendance?.checked_in} sub={`${auto.attendance?.attendance_rate ?? 0}% attendance`} />
            <Stat label="Members" value={auto.member_mix?.members} sub={`${auto.member_mix?.non_members ?? 0} non-members`} />
            <Stat label="First-time" value={auto.first_time_returning?.first_time} sub={`${auto.first_time_returning?.returning ?? 0} returning`} />
          </StatRow>
          <Field label="Notable founders, partners & guests (curated)">
            <div className="space-y-2" data-testid="rp-guests">
              {(report.notable_guests || []).map((g, i) => (
                <div key={g._k || `guest-${i}`} className="grid grid-cols-[1fr_1fr_120px_auto] gap-2 items-center">
                  <input className="input-field text-sm" placeholder="Name" value={g.name || ""} onChange={(e) => setGuest(i, "name", e.target.value)} disabled={isFinal} />
                  <input className="input-field text-sm" placeholder="Company / role" value={g.org || ""} onChange={(e) => setGuest(i, "org", e.target.value)} disabled={isFinal} />
                  <select className="input-field text-sm" value={g.type || "member"} onChange={(e) => setGuest(i, "type", e.target.value)} disabled={isFinal}>
                    <option value="member">Member</option><option value="founder">Founder</option><option value="partner">Partner</option><option value="guest">Guest</option>
                  </select>
                  <button onClick={() => removeGuest(i)} disabled={isFinal} className="btn-ghost text-xs"><X size={11} /></button>
                </div>
              ))}
              <button onClick={addGuest} disabled={isFinal} className="btn-ghost text-xs" data-testid="rp-add-guest"><Plus size={11} />Add guest</button>
            </div>
          </Field>
        </ReportSection>

        {/* Section: Purpose & Outcomes */}
        <ReportSection title="Purpose & Outcomes" testId="rp-purpose">
          <Field label="Primary objective" hint={event.objective ? `Pre-filled from event: "${event.objective}"` : "Set at event creation for auto-fill."}>
            <textarea rows={2} className="input-field resize-y" value={report.objective || event.objective || ""} onChange={(e) => saveField("objective", e.target.value)} disabled={isFinal} data-testid="rp-objective" placeholder="What was this event meant to achieve?" />
          </Field>
          <Field label="Did we achieve the goals?">
            <textarea rows={3} className="input-field resize-y" value={report.outcomes || ""} onChange={(e) => saveField("outcomes", e.target.value)} disabled={isFinal} data-testid="rp-outcomes" placeholder="What happened relative to the goals?" />
          </Field>
          <Field label="Key conversations, intros and opportunities">
            <textarea rows={3} className="input-field resize-y" value={report.key_conversations || ""} onChange={(e) => saveField("key_conversations", e.target.value)} disabled={isFinal} placeholder="Notable connections, deals sparked, intros made." />
          </Field>
          <Field label="Follow-up actions & potential ROI" hint={`${auto.conversions?.converted ?? 0} attendee(s) have joined Venture Dock since this event.`}>
            <textarea rows={3} className="input-field resize-y" value={report.followup_roi || ""} onChange={(e) => saveField("followup_roi", e.target.value)} disabled={isFinal} placeholder="Concrete next steps + expected value." />
          </Field>
        </ReportSection>

        {/* Section: Feedback */}
        <ReportSection title="Attendee Feedback" testId="rp-feedback">
          {auto.feedback?.sent === 0 ? (
            <div className="text-sm text-muted-foreground">Feedback survey not sent yet. Send it from the <span className="chip chip-cream text-[10px]">Feedback</span> tab first.</div>
          ) : (
            <>
              <StatRow>
                <Stat label="NPS" value={auto.feedback?.nps ?? "-"} sub={`${auto.feedback?.nps_count ?? 0} responses`} />
                <Stat label="Promoters" value={auto.feedback?.promoters ?? 0} />
                <Stat label="Passives" value={auto.feedback?.passives ?? 0} />
                <Stat label="Detractors" value={auto.feedback?.detractors ?? 0} />
              </StatRow>
              <Field label="What worked well (auto)">
                <pre className="whitespace-pre-wrap font-mono text-xs p-3 rounded-xl bg-secondary max-h-40 overflow-y-auto">{auto.feedback?.q1_summary || "No responses yet."}</pre>
              </Field>
              <Field label="What could be improved (auto)">
                <pre className="whitespace-pre-wrap font-mono text-xs p-3 rounded-xl bg-secondary max-h-40 overflow-y-auto">{auto.feedback?.q2_summary || "No responses yet."}</pre>
              </Field>
            </>
          )}
          <Field label="Suggestions for future events">
            <textarea rows={3} className="input-field resize-y" value={report.suggestions || ""} onChange={(e) => saveField("suggestions", e.target.value)} disabled={isFinal} placeholder="Curate the best ideas from comments + your own additions." />
          </Field>
        </ReportSection>

        {/* Section: Lessons Learned */}
        <ReportSection title="Lessons Learned" testId="rp-lessons">
          <div className="grid md:grid-cols-2 gap-3">
            {LESSON_KEYS.map(({ k, label }) => (
              <div key={k}>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
                <textarea rows={2} className="input-field resize-y text-sm" value={(report.lessons || {})[k] || ""} onChange={(e) => setLesson(k, e.target.value)} disabled={isFinal} data-testid={`rp-lesson-${k}`} placeholder={`Notes about ${label.toLowerCase()}…`} />
              </div>
            ))}
          </div>
        </ReportSection>

        {/* Section: Follow-up */}
        <ReportSection title="Follow-up" testId="rp-followup">
          <Field label="Upcoming events shared with attendees (auto)">
            {auto.upcoming?.length ? (
              <ul className="text-sm space-y-1.5">
                {auto.upcoming.map((u) => (
                  <li key={u.id} className="flex items-center gap-2">
                    <span className="font-medium">{u.title}</span>
                    <span className="font-mono text-xs text-muted-foreground">· {fmtDatePT(u.start_at, { month: "short", day: "numeric" })}</span>
                  </li>
                ))}
              </ul>
            ) : <div className="text-sm text-muted-foreground">No upcoming events on the calendar.</div>}
          </Field>
        </ReportSection>

        {/* Section: Marketing Recap */}
        <ReportSection title="Marketing Recap" testId="rp-marketing">
          <Field label="How it was promoted beforehand">
            <textarea rows={2} className="input-field resize-y" value={report.marketing_promo || ""} onChange={(e) => saveField("marketing_promo", e.target.value)} disabled={isFinal} placeholder="Channels, cadence, key posts." />
          </Field>
          <Field label="Channel performance (auto)">
            {auto.channels?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-xs uppercase tracking-widest text-muted-foreground"><th className="text-left py-2">Channel</th><th className="text-left py-2">Source</th><th className="text-left py-2">Medium</th><th className="text-right py-2">Regs</th><th className="text-right py-2">Check-ins</th></tr></thead>
                  <tbody>
                    {auto.channels.map((c) => (
                      <tr key={`${c.utm_source}|${c.utm_medium || ""}|${c.utm_campaign || ""}`} className="border-t border-white/40" data-testid={`rp-channel-${c.utm_source}`}>
                        <td className="py-2">{c.label}</td>
                        <td className="py-2 font-mono text-xs">{c.utm_source}</td>
                        <td className="py-2 font-mono text-xs">{c.utm_medium || "-"}</td>
                        <td className="py-2 text-right font-mono">{c.registrations}</td>
                        <td className="py-2 text-right font-mono">{c.checkins}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <div className="text-sm text-muted-foreground">No tracked share links used for this event. Create some in Overview → Share Links.</div>}
          </Field>
          <Field label="Social engagement (manual entry)">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-2">
              {["views", "likes", "comments", "shares"].map((k) => (
                <div key={k}>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{k}</div>
                  <input type="number" className="input-field text-sm" value={(report.marketing_engagement || {})[k] ?? ""} onChange={(e) => setEngagement(k, e.target.value === "" ? null : Number(e.target.value))} disabled={isFinal} data-testid={`rp-engagement-${k}`} />
                </div>
              ))}
            </div>
            <textarea rows={2} className="input-field resize-y text-sm" placeholder="Notes on social performance…" value={(report.marketing_engagement || {}).notes || ""} onChange={(e) => setEngagement("notes", e.target.value)} disabled={isFinal} />
          </Field>
          <Field label="Photos, videos and posts">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="rp-media-grid">
              {media.map((m) => (
                <div key={m.id} className="relative group rounded-xl overflow-hidden border border-white/60 bg-white/40">
                  {m.kind === "image" ? (
                    <img src={resolveImageUrl(m.url)} alt="" className="w-full h-24 object-cover" />
                  ) : (
                    <a href={m.url} target="_blank" rel="noreferrer" className="p-3 flex items-center gap-2 text-xs text-primary font-mono h-24">
                      <Link2 size={13} /><span className="truncate">{m.url.replace(/^https?:\/\//, "")}</span>
                    </a>
                  )}
                  <button onClick={() => removeMedia(m.id)} disabled={isFinal} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/40 text-white opacity-0 group-hover:opacity-100 transition flex items-center justify-center" title="Remove"><X size={11} /></button>
                </div>
              ))}
              <label className={`h-24 rounded-xl border-2 border-dashed border-white/60 bg-white/30 flex flex-col items-center justify-center text-xs text-muted-foreground cursor-pointer hover:bg-white/50 transition ${isFinal ? "opacity-50 cursor-not-allowed" : ""}`}>
                <ImageIcon size={16} />
                <span className="mt-1">Upload image</span>
                <input type="file" accept="image/*" className="hidden" disabled={isFinal} onChange={(e) => uploadImage(e.target.files?.[0])} data-testid="rp-upload-image" />
              </label>
              <LinkAdder onAdd={addLink} disabled={isFinal} />
            </div>
          </Field>
          <Field label="How to better leverage future events">
            <textarea rows={2} className="input-field resize-y" value={report.leverage_notes || ""} onChange={(e) => saveField("leverage_notes", e.target.value)} disabled={isFinal} placeholder="Ideas for the next lunch / retro." />
          </Field>
        </ReportSection>
      </div>

      {/* Sticky right rail */}
      <aside className="space-y-4 lg:sticky lg:top-4 self-start" data-testid="report-rail">
        <div className="glass-panel p-4 space-y-3">
          <Overline>Actions</Overline>
          <button onClick={exportPdf} disabled={exporting} className="btn-primary w-full text-sm" data-testid="rp-export-pdf">
            <Download size={13} />{exporting ? "Generating…" : `Generate PDF (v${report.version})`}
          </button>
          <button onClick={toggleFinal} className="btn-ghost w-full text-sm" data-testid="rp-toggle-final">
            {isFinal ? <><Unlock size={12} />Unlock (make Draft)</> : <><Lock size={12} />Mark Final & lock</>}
          </button>
        </div>
        <div className="glass-panel p-4">
          <Overline>Autosave</Overline>
          <div className="mt-2 text-xs text-muted-foreground">
            {savingKey ? <span className="text-primary">Saving {savingKey}…</span> : "All changes saved"}
          </div>
        </div>
        {data.versions?.length > 1 && (
          <div className="glass-panel p-4" data-testid="rp-versions">
            <Overline>Version history</Overline>
            <ul className="mt-2 space-y-1 text-xs font-mono">
              {data.versions.map((v) => (
                <li key={v.id} className="flex items-center justify-between">
                  <span>v{v.version} · {v.status}</span>
                  <span className="text-muted-foreground">{new Date(v.created_at).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {event.source === "luma" && (
          <div className="glass-panel p-4 text-xs text-muted-foreground" data-testid="rp-luma-note">
            <div className="font-medium text-foreground mb-1">Luma event</div>
            Auto fields read from synced Luma data. If Luma attendance was not tracked, the attendance number reflects registrations only.
          </div>
        )}
      </aside>
    </div>
  );
}

/* Small helpers */
function ReportSection({ title, testId, children }) {
  return (
    <div className="glass-panel p-6" data-testid={testId}>
      <Overline>{title}</Overline>
      <div className="mt-4 space-y-4">{children}</div>
    </div>
  );
}
function StatRow({ children }) {
  return <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{children}</div>;
}
function Stat({ label, value, sub }) {
  return (
    <div className="p-4 rounded-xl border border-white/60 bg-white/40">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="stat-value text-2xl mt-1">{value ?? "-"}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-1 font-mono">{sub}</div>}
    </div>
  );
}
function Field({ label, hint, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-muted-foreground mt-1 font-mono">{hint}</div>}
    </div>
  );
}
function LinkAdder({ onAdd, disabled }) {
  const [v, setV] = useState("");
  return (
    <div className={`h-24 rounded-xl border-2 border-dashed border-white/60 bg-white/30 p-2 flex flex-col ${disabled ? "opacity-50" : ""}`}>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Paste link</div>
      <input className="input-field text-xs mt-1" placeholder="https://…" value={v} onChange={(e) => setV(e.target.value)} disabled={disabled} data-testid="rp-add-link" />
      <button onClick={() => { onAdd(v); setV(""); }} disabled={disabled || !v.trim()} className="btn-ghost text-xs mt-1"><Plus size={10} />Add</button>
    </div>
  );
}
